import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Spinner } from '../ui/Spinner';
import {
  getOrphanLabelImpact,
  pruneOrphanCampaignLabels,
  type OrphanLabelImpact,
  type OrphanPruneResult,
} from '../../modules/campaigns/api';
import {
  pruneRowLocalAssetLabels,
  pruneRowOrganicAssetLabels,
} from '../../modules/rollouts/api';

// Ad Preview Mockups Stage 1 §13.17. Confirmation modal for orphan
// AssetLabel prune. Two modes:
//
//   - 'campaign' — Campaign-level Meta Assets prune. Fetches impact
//     counts (cascade preview) on mount. Confirm triggers the
//     server-side prune + cascade.
//
//   - 'row' — Row-level Local Assets prune. No cascade (localAssetLabels
//     are row-scoped). The caller supplies the local orphan count (it
//     already has both the labels array and the live listing — no
//     server round-trip needed for the preview).
//
// Shell follows TypeDeleteConfirmationModal's pattern (fixed overlay +
// centred card). No type-to-confirm gate — prune is recoverable (AM
// can re-author labels), unlike hard delete.

type CommonProps = {
  // The number of orphans on the immediate surface (Campaign or row).
  // Used in the headline + button label so even before the preview
  // call returns, the AM sees how many are about to be removed.
  orphanCount: number;
  busy?: boolean;
  onCancel: () => void;
};

type CampaignProps = CommonProps & {
  mode: 'campaign';
  campaignId: string;
  campaignName: string;
  // Confirm callback fires with the server's result so the caller
  // can surface a success message + refetch labels.
  onConfirm: (result: OrphanPruneResult) => void;
};

type RowProps = CommonProps & {
  mode: 'row';
  rolloutId: string;
  rowId: string;
  // OrganicAssets §9.3: which row-scoped asset surface to prune. 'local'
  // and 'organic' hit their respective endpoints.
  assetKind: 'local' | 'organic';
  // No cascade — the caller already knows the row context. Used in
  // the confirm-button-label and the headline; not passed to the
  // server (the prune is fully derived from the row id).
  rowLabel: string;
  onConfirm: () => void;
};

type Props = CampaignProps | RowProps;

export function OrphanLabelPruneModal(props: Props) {
  const { orphanCount, busy = false, onCancel } = props;
  // Campaign-mode cascade preview. Null until fetched; loading state
  // implicit (null + no error). Errors surface inline so the AM
  // doesn't dismiss-and-retry blindly.
  const [impact, setImpact] = useState<OrphanLabelImpact | null>(null);
  const [impactError, setImpactError] = useState<string | null>(null);
  const [pruneError, setPruneError] = useState<string | null>(null);
  const [pruning, setPruning] = useState(false);

  useEffect(() => {
    if (props.mode !== 'campaign') return;
    let cancelled = false;
    getOrphanLabelImpact(props.campaignId).then(
      (i) => {
        if (!cancelled) setImpact(i);
      },
      (err) => {
        if (!cancelled) {
          setImpactError(
            err instanceof Error
              ? err.message
              : 'Failed to load cascade impact.',
          );
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // props.campaignId is the only dependency; mode-gating happens
    // above. Disabling exhaustive-deps because TS narrows away on the
    // mode check and the linter doesn't follow that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode === 'campaign' ? props.campaignId : null]);

  const isCampaign = props.mode === 'campaign';

  async function handleConfirm() {
    setPruning(true);
    setPruneError(null);
    try {
      if (props.mode === 'campaign') {
        const result = await pruneOrphanCampaignLabels(props.campaignId);
        props.onConfirm(result);
      } else {
        if (props.assetKind === 'organic') {
          await pruneRowOrganicAssetLabels(props.rolloutId, props.rowId);
        } else {
          await pruneRowLocalAssetLabels(props.rolloutId, props.rowId);
        }
        props.onConfirm();
      }
    } catch (err) {
      setPruneError(
        err instanceof Error ? err.message : 'Prune failed. Try again.',
      );
      setPruning(false);
    }
  }

  const disabled = busy || pruning || (isCampaign && impact === null && !impactError);
  const labelCopy = orphanCount === 1 ? 'label' : 'labels';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Clear orphan labels"
    >
      <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-slate-900">
          Clear {orphanCount} orphan {labelCopy}?
        </h3>
        <p className="mt-2 text-sm text-slate-600">
          {isCampaign
            ? 'These labels point to files that no longer exist in this folder. Clearing them will:'
            : 'These labels point to files that no longer exist in this folder. Clearing them removes them from this row only.'}
        </p>

        {isCampaign && (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
            <li>Remove the labels from this Campaign</li>
            <li>
              Also remove from active/draft rollouts that snapshot this
              Campaign
            </li>
            <li>
              Preserve labels on archived rollouts (historical record)
            </li>
          </ul>
        )}

        <div className="mt-4 space-y-1 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p>
            <span className="text-slate-500">
              {isCampaign ? 'Campaign:' : 'Row:'}
            </span>{' '}
            <span className="font-medium text-slate-900">
              {isCampaign
                ? (props as CampaignProps).campaignName
                : (props as RowProps).rowLabel}
            </span>
          </p>
          {isCampaign && (
            <p>
              <span className="text-slate-500">Rollouts to be updated:</span>{' '}
              {impact === null && !impactError && (
                <span className="inline-flex items-center gap-1">
                  <Spinner /> <span className="text-slate-400">counting…</span>
                </span>
              )}
              {impact && (
                <span className="font-medium text-slate-900">
                  {impact.affectedRolloutCount}
                </span>
              )}
              {impact && impact.affectedRowCount > 0 && (
                <span className="text-slate-500">
                  {' '}
                  · {impact.affectedRowCount}{' '}
                  {impact.affectedRowCount === 1 ? 'row' : 'rows'}
                </span>
              )}
            </p>
          )}
          {isCampaign && impact && impact.historicalRowsSkipped > 0 && (
            <p>
              <span className="text-slate-500">
                Preserved on archived rollouts:
              </span>{' '}
              <span className="font-medium text-slate-900">
                {impact.historicalRowsSkipped}
              </span>
            </p>
          )}
          {impactError && (
            <p className="text-amber-800">
              Couldn't load cascade preview: {impactError}. The prune will
              still work — counts unknown until it runs.
            </p>
          )}
        </div>

        {pruneError && (
          <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {pruneError}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={pruning}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={disabled}>
            {pruning
              ? 'Clearing…'
              : `Clear ${orphanCount} orphan ${labelCopy}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
